/**
 * Built-in scenario definitions
 * Ported from scenario-examples.js
 */

import type { Scenario } from '../types/scenario';

export const SCENARIOS: Record<string, Scenario> = {
  brownian_motion: {
    name: 'Brownian Motion',
    version: '1.0',
    description: 'Simple diffusion of particles in a box',
    world: {
      bounds: { min: [-200, -200, -200], max: [200, 200, 200] },
      cellSize: 10,
      temperature: 310,
      dt: 0.001,
    },
    entities: [
      {
        count: 5000,
        archetype: {
          id: 0,
          name: 'Small Protein',
          mass: 10,
          radius: 2,
          charge: 0,
          hydrophobicity: 0,
          diffusionCoeff: 50,
        },
        randomSpawn: { min: -150, max: 150 },
      },
      {
        count: 500,
        archetype: {
          id: 1,
          name: 'Large Protein',
          mass: 100,
          radius: 5,
          charge: 0,
          hydrophobicity: 0,
          diffusionCoeff: 10,
        },
        randomSpawn: { min: -100, max: 100 },
      },
    ],
  },

  lipid_bilayer: {
    name: 'Lipid Bilayer Self-Assembly',
    version: '1.0',
    description: 'Phospholipids self-assembling into a bilayer membrane',
    world: {
      bounds: { min: [-100, -100, -100], max: [100, 100, 100] },
      cellSize: 5,
      temperature: 310,
      dt: 0.0005,
    },
    entities: [
      {
        count: 3000,
        archetype: {
          id: 0,
          name: 'Phospholipid',
          mass: 0.75,
          radius: 1,
          charge: -1,
          hydrophobicity: 0.7,
          diffusionCoeff: 30,
        },
        randomSpawn: { min: -50, max: 50 },
      },
      {
        count: 10000,
        archetype: {
          id: 1,
          name: 'Water Cluster',
          mass: 0.5,
          radius: 0.5,
          charge: 0,
          hydrophobicity: -1,
          diffusionCoeff: 100,
        },
        randomSpawn: { min: -80, max: 80 },
      },
    ],
  },

  ion_channel: {
    name: 'Ion Channel',
    version: '1.0',
    description: 'Ions interacting with a channel protein in a membrane',
    world: {
      bounds: { min: [-100, -100, -100], max: [100, 100, 100] },
      cellSize: 5,
      temperature: 310,
      dt: 0.0005,
    },
    entities: [
      {
        count: 2000,
        archetype: {
          id: 0,
          name: 'Lipid',
          mass: 0.75,
          radius: 1,
          charge: -1,
          hydrophobicity: 0.7,
          diffusionCoeff: 30,
        },
        randomSpawn: { min: -50, max: 50 },
      },
      {
        count: 10,
        archetype: {
          id: 1,
          name: 'Channel Protein',
          mass: 50,
          radius: 3,
          charge: 0,
          hydrophobicity: 0.3,
          diffusionCoeff: 5,
        },
        randomSpawn: { min: -20, max: 20 },
      },
      {
        count: 1000,
        archetype: {
          id: 2,
          name: 'K+ Ion',
          mass: 0.04,
          radius: 0.2,
          charge: 1,
          hydrophobicity: -1,
          diffusionCoeff: 100,
        },
        randomSpawn: { min: -80, max: -10 },
      },
      {
        count: 1000,
        archetype: {
          id: 3,
          name: 'Cl- Ion',
          mass: 0.035,
          radius: 0.2,
          charge: -1,
          hydrophobicity: -1,
          diffusionCoeff: 100,
        },
        randomSpawn: { min: 10, max: 80 },
      },
    ],
  },

  protein_binding: {
    name: 'Protein Binding',
    version: '1.0',
    description: 'Two protein types with complementary binding sites',
    world: {
      bounds: { min: [-150, -150, -150], max: [150, 150, 150] },
      cellSize: 10,
      temperature: 310,
      dt: 0.001,
    },
    entities: [
      {
        count: 500,
        archetype: {
          id: 0,
          name: 'Kinase',
          mass: 30,
          radius: 3,
          charge: 2,
          hydrophobicity: 0.2,
          diffusionCoeff: 20,
        },
        randomSpawn: { min: -100, max: 100 },
      },
      {
        count: 1000,
        archetype: {
          id: 1,
          name: 'Substrate',
          mass: 15,
          radius: 2,
          charge: -2,
          hydrophobicity: 0.1,
          diffusionCoeff: 35,
        },
        randomSpawn: { min: -100, max: 100 },
      },
    ],
  },

  logic_gate_and: {
    name: 'AND Logic Gate',
    version: '1.0',
    description: 'Molecular AND gate using binding cooperativity',
    world: {
      bounds: { min: [-50, -50, -50], max: [50, 50, 50] },
      cellSize: 5,
      temperature: 310,
      dt: 0.001,
    },
    entities: [
      {
        count: 10,
        archetype: {
          id: 0,
          name: 'Gate Protein',
          mass: 50,
          radius: 4,
          charge: 0,
          hydrophobicity: 0,
          diffusionCoeff: 5,
        },
        randomSpawn: { min: -10, max: 10 },
      },
      {
        count: 100,
        archetype: {
          id: 1,
          name: 'Input A',
          mass: 5,
          radius: 1,
          charge: 1,
          hydrophobicity: 0,
          diffusionCoeff: 50,
        },
        randomSpawn: { min: -40, max: -10 },
      },
      {
        count: 100,
        archetype: {
          id: 2,
          name: 'Input B',
          mass: 5,
          radius: 1,
          charge: -1,
          hydrophobicity: 0,
          diffusionCoeff: 50,
        },
        randomSpawn: { min: 10, max: 40 },
      },
    ],
  },

  logic_gate_or: {
    name: 'OR Logic Gate',
    version: '1.0',
    description: 'Molecular OR gate - activation by either input',
    world: {
      bounds: { min: [-50, -50, -50], max: [50, 50, 50] },
      cellSize: 5,
      temperature: 310,
      dt: 0.001,
    },
    entities: [
      {
        count: 20,
        archetype: {
          id: 0,
          name: 'Gate Protein',
          mass: 40,
          radius: 3.5,
          charge: -1,
          hydrophobicity: 0,
          diffusionCoeff: 5,
        },
        randomSpawn: { min: -10, max: 10 },
      },
      {
        count: 50,
        archetype: {
          id: 1,
          name: 'Input A',
          mass: 8,
          radius: 1.5,
          charge: 2,
          hydrophobicity: 0,
          diffusionCoeff: 40,
        },
        randomSpawn: { min: -40, max: 0 },
      },
      {
        count: 50,
        archetype: {
          id: 2,
          name: 'Input B',
          mass: 8,
          radius: 1.5,
          charge: 2,
          hydrophobicity: 0.3,
          diffusionCoeff: 40,
        },
        randomSpawn: { min: 0, max: 40 },
      },
    ],
  },

  cytoskeleton: {
    name: 'Cytoskeleton Assembly',
    version: '1.0',
    description: 'Actin monomers polymerizing into filaments',
    world: {
      bounds: { min: [-200, -200, -200], max: [200, 200, 200] },
      cellSize: 10,
      temperature: 310,
      dt: 0.001,
    },
    entities: [
      {
        count: 2000,
        archetype: {
          id: 0,
          name: 'Actin-G',
          mass: 42,
          radius: 2.7,
          charge: -7,
          hydrophobicity: -0.1,
          diffusionCoeff: 15,
        },
        randomSpawn: { min: -150, max: 150 },
      },
      {
        count: 5000,
        archetype: {
          id: 1,
          name: 'ATP',
          mass: 0.5,
          radius: 0.5,
          charge: -4,
          hydrophobicity: -0.5,
          diffusionCoeff: 80,
        },
        randomSpawn: { min: -150, max: 150 },
      },
    ],
  },
};

export function getScenarioByKey(key: string): Scenario | null {
  return SCENARIOS[key] ?? null;
}

export function getScenarioKeys(): string[] {
  return Object.keys(SCENARIOS);
}
